import { isNone, noop, Props } from '@crocodille/common';
import cn from 'classnames';
import React, {
  Children,
  ForwardedRef,
  forwardRef,
  FunctionComponent,
  JSXElementConstructor,
  ReactElement,
  useMemo,
} from 'react';

import styles from './button.module.scss';

type CommonButtonProps = {
  readonly display?: 'inline' | 'block';
  readonly variant?: 'primary' | 'secondary' | 'basic' | 'transparent';
  readonly size?: 'small' | 'medium' | 'large';
  readonly symmetrical?: boolean;
  readonly round?: boolean;
};

export type ButtonButtonProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> &
  CommonButtonProps & {
    readonly as?: 'button';
  };

export type AnchorButtonProps = React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
> &
  CommonButtonProps & {
    readonly as: 'a';
    readonly disabled?: boolean;
  };

export type ButtonProps = ButtonButtonProps | AnchorButtonProps;

function isReactElementOfType<
  T extends JSXElementConstructor<unknown> | FunctionComponent,
>(value: unknown, type: T): value is ReactElement<Props<T>, T> {
  return (
    !!value &&
    typeof value === 'object' &&
    'type' in value &&
    (value as { type?: unknown }).type === type
  );
}

const ButtonElement = forwardRef(
  (
    {
      as = 'button',
      className,
      children,
      type,
      display = 'block',
      variant = 'primary',
      size = 'medium',
      symmetrical = true,
      round = false,
      ...props
    }: ButtonProps,
    ref: ForwardedRef<HTMLButtonElement> | ForwardedRef<HTMLAnchorElement>,
  ) => {
    const hasIconOnRight = useMemo(
      () =>
        Children.toArray(children).some(
          (child) =>
            isReactElementOfType(child, Icon) &&
            (isNone(child.props.placement) ||
              child.props.placement === 'right'),
        ),
      [children],
    );

    const hasIconOnLeft = useMemo(
      () =>
        Children.toArray(children).some(
          (child) =>
            isReactElementOfType(child, Icon) &&
            child.props.placement === 'left',
        ),
      [children],
    );

    const withIconOnRight = (symmetrical && hasIconOnLeft) || hasIconOnRight;
    const withIconOnLeft = (symmetrical && hasIconOnRight) || hasIconOnLeft;

    if (as === 'a') {
      const { disabled = false, onClick = noop, ...anchorProps } = props;

      return (
        <a
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              e.stopPropagation();
            }
            (onClick as React.MouseEventHandler<HTMLAnchorElement>)(e);
          }}
          className={cn(
            styles.button,
            styles[variant],
            styles[display],
            styles[size],
            {
              [styles.withIconOnLeft]: withIconOnLeft,
              [styles.withIconOnRight]: withIconOnRight,
              [styles.isSymmetrical]: symmetrical,
              [styles.isDisabled]: disabled,
              [styles.isRound]: round,
            },
            className,
          )}
          {...(anchorProps as React.DetailedHTMLProps<
            React.AnchorHTMLAttributes<HTMLAnchorElement>,
            HTMLAnchorElement
          >)}
        >
          {children}
        </a>
      );
    } else {
      return (
        <button
          ref={ref as ForwardedRef<HTMLButtonElement>}
          type={
            (type as React.DetailedHTMLProps<
              React.ButtonHTMLAttributes<HTMLButtonElement>,
              HTMLButtonElement
            >['type']) ?? 'button'
          }
          className={cn(
            styles.button,
            styles[variant],
            styles[display],
            styles[size],
            {
              [styles.withIconOnLeft]: withIconOnLeft,
              [styles.withIconOnRight]: withIconOnRight,
              [styles.isSymmetrical]: symmetrical,
              [styles.isRound]: round,
            },
            className,
          )}
          {...(props as React.DetailedHTMLProps<
            React.ButtonHTMLAttributes<HTMLButtonElement>,
            HTMLButtonElement
          >)}
        >
          {children}
        </button>
      );
    }
  },
);

ButtonElement.displayName = 'Button';

const Icon: React.FC<
  React.PropsWithChildren<{ readonly placement?: 'left' | 'right' }>
> = ({ children, placement = 'right' }) => {
  return (
    <span role="presentation" className={cn(styles.icon, styles[placement])}>
      {children}
    </span>
  );
};

const Button = Object.assign(ButtonElement, { Icon });

export default Button;
